import {
	clubStatus,
	DataCollection,
	DataVehicles,
	GetPersonUser,
	normalizeVouchers,
	PersonBenefits,
	UserStatus,
	VehicleStatus,
} from '@esportal/portal-service';
import { useContext, useEffect, useState } from 'react';
import { PortalContext } from '../context/portal-context';
import React from 'react';

export const useUserData = () => {
	const { breacrumbService, pageInfoService, portalService } = useContext(PortalContext);

	const [userData, setUserData] = useState<GetPersonUser | null>(null);
	const [isUpdating, setIsUpdating] = useState<boolean>(false);
	const [hasError, setHasError] = useState<boolean>(false);
	const [hasToLog, setHasToLog] = useState<boolean>(false);
	const [validatingIDKit, setValidatingIDKit] = React.useState<boolean>(true);

	const [userVehicles, setUserVehicles] = React.useState<DataVehicles | null>(null);
	const [personBenefits, setPersonBenefits] = React.useState<PersonBenefits | null>(null);
	const [dataCollection, setDataCollection] = React.useState<DataCollection | null>(null);

	const vehicleToBeCreated = userVehicles?.vehicles.find((vehicle) => vehicle.status === VehicleStatus.TO_BE_CREATED);
	const userClimaproStatus = userData?.portalUser.data.climapro.status;
	const userClubIsAccepted = userData?.portalUser.data.clubVW.isAccepted;
	const userClubStatus = userData?.portalUser.data.clubVW.status;
	const canLoadVehicles = userClimaproStatus === UserStatus.OK || userClimaproStatus === UserStatus.PREMATCH;
	const needsVehicles = canLoadVehicles && userClubIsAccepted;
	const needsBenefits = userClubStatus === clubStatus.MEMBER;
	const isLoading =
		validatingIDKit || !userData || (needsVehicles && !userVehicles) || (needsBenefits && !personBenefits);

	// Set up breadcrumb and page info on component mount.
	useEffect(() => {
		breacrumbService.set([]);
		pageInfoService.setPageInfo();
	}, [pageInfoService, breacrumbService]);

	// Fetch user data from the portal service and manage loading and error states.
	useEffect(() => {
		const subscription = portalService.getPerson().subscribe({
			next: (data: GetPersonUser | null) => {
				if (data?.portalUser.data.climapro.status === UserStatus.ERROR) {
					setHasError(true);
					setIsUpdating(false);
				}
				setUserData(data);
				setIsUpdating(false);
			},
			error: () => {
				setHasError(true);
				setIsUpdating(false);
			},
		});
		return () => subscription.unsubscribe();
	}, [portalService]);

	// Subscribe to the `hasToLog` observable from `portalService` and update the `hasToLog` state.
	useEffect(() => {
		const subscription = portalService.hasToLog().subscribe(setHasToLog);
		return () => subscription.unsubscribe();
	}, [portalService]);

	// Subscribe to the `isValidatingIDKit` observable from `portalService` and update the `validatingIDKit` state.
	useEffect(() => {
		const subscription = portalService.isValidatingIDKit().subscribe(setValidatingIDKit);
		return () => subscription.unsubscribe();
	}, [portalService]);

	// Fetch user vehicles data when the user's climapro status is OK or PREMATCH.
	useEffect(() => {
		if (
			userData?.portalUser.data.climapro.status == UserStatus.OK ||
			userData?.portalUser.data.climapro.status == UserStatus.PREMATCH
		) {
			const subscription = portalService.getPersonVehicles().subscribe({
				next: (data: DataVehicles | null) => {
					setUserVehicles(data);
				},
				error: () => {
					setHasError(true);
				},
			});
			return () => subscription.unsubscribe();
		}
		return undefined;
	}, [portalService, userData]);

	// Fetch person benefits data when the user's club status is MEMBER.
	useEffect(() => {
		if (userData?.portalUser.data.clubVW.status == clubStatus.MEMBER) {
			const subscription = portalService.getPersonBenefits().subscribe((data: PersonBenefits | null) => {
				setPersonBenefits(data);
			});
			return () => subscription.unsubscribe();
		}
		return undefined;
	}, [portalService, userData]);

	// Fetch data collection when the user's club status is MEMBER.
	useEffect(() => {
		if (userData?.portalUser.data.clubVW.status == clubStatus.MEMBER) {
			const subscription = portalService.getPersonDataCollection().subscribe((data: DataCollection | null) => {
				setDataCollection(data);
			});
			return () => {
				subscription.unsubscribe();
			};
		}
		return;
	}, [dataCollection, portalService, userData]);

	const normalizedVouchers = personBenefits ? normalizeVouchers(personBenefits.benefits) : null;

	return {
		userData,
		userVehicles,
		personBenefits,
		dataCollection,
		isUpdating,
		hasError,
		hasToLog,
		validatingIDKit,
		setIsUpdating,
		vehicleToBeCreated,
		isLoading,
		normalizedVouchers,
	};
};
